// Cookie.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { Card } from '../components/UI/card';
import { Button } from '../components/UI/button';
import logo from '../assets/FLUXLAB-Logo.png';
import { Phone, Mail } from 'lucide-react';
import { Link } from "react-router-dom";

const Cookie = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white text-gray-800">
      {/* Navigation - simplified, assuming shared or no motion for now */}
      <motion.nav
  className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm"
  
  initial={{ y: -100 }}
  animate={{ y: 0 }}
  transition={{ duration: 0.6 }}
>
  <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
    {/* 🟢 Logo Section */}
    <div
      className="flex items-center gap-3 cursor-pointer"
      onClick={scrollToTop}
    >
      <img
        src={logo}
        alt="Company Logo"
        className="w-[110px] h-auto" // Increased by 30%
      />
    </div>

    {/* 🔵 Navigation Links */}
    <div className="hidden md:flex items-center gap-8">
       <a
        href="/"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Home
      </a>
      <a
        href="#about"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        About
      </a>
      <a
        href="#services"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Services
      </a>
      <a
        href="#why-us"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Why Us
      </a>
      <a
        href="#contact"
        className="text-[#2563eb] hover:text-gray-500 font-medium transition-colors"
      >
        Contact
      </a>
    </div>

    {/* 🔴 Call-to-Action Button */}
    <a href="#contact">
      <Button className="bg-[#2563eb] hover:bg-red-700 text-white flex items-center">
        <Phone className="w-4 h-4 mr-2" />
        Get Quote
      </Button>
    </a>
  </div>
</motion.nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold mb-6 mt-12"
          >
            Cookie of Service
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl italic tracking-wide font-light"
          >
            Important legal information
          </motion.p>
        </div>
      </section>

      {/* Cookie Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="prose prose-lg max-w-none">
         <p>
  FLUXLAB uses Cookies and other similar technologies (collectively, 
  “Cookies”) to personalize your experience on the website and the advertisements that 
  may be displayed.
</p>

<p>
  <strong>What is a Cookie?</strong> A cookie is a small text file placed on your device 
  (e.g., computer or smartphone) via your web browser at the request of the websites you 
  visit or applications you use. It stores information on your device for a limited 
  period of time. Cookies allow FLUXLAB to recognize you and your device 
  each time you visit our Platforms.
</p>

<p><strong>Essential Cookies:</strong> Some Cookies are optional, but certain Cookies used 
by us are Essential for the proper functioning of the Website and to provide the best 
services to you. You cannot opt out of these Cookies because without them, 
FLUXLAB wouldn't function properly. We use Essential Cookies for the 
following purposes:</p>

<ul style={{ listStyle: "disc", marginLeft: "30px" }}>
  <li>
    Recognise whether you are a first-time visitor or a return visitor to our Website and 
    tailor the presentation of our services, such as recalling your language and currency 
    preferences or your most recent searches.
  </li>
  <li>
    Remember information essential to displaying your search results, including your 
    language and currency preferences or certain product features made available to you.
  </li>
  <li>
    Remember your search criteria, such as routes and dates, as you navigate between pages 
    or move to specific sections within our Platforms.
  </li>
  <li>
    Gather important information on how our products and services are used, which helps 
    influence product decisions, updates, and improvements.
  </li>
  <li>
    Ensure our services are secure from automated agents, bots, and fraudulent activity.
  </li>
  <li>
    Provide login functionality and customer support services.
  </li>
</ul>

<h4 style={{ color: "#0a4d68", marginTop: "30px" }}>Opt-Out</h4>
<p>
  When you access our services for the first time on your device, we ask you to agree to 
  our use of Cookies and similar technologies in line with this policy. If you don't agree 
  or you change your mind later, you can withdraw your consent using the methods outlined 
  in this Policy, or you may choose to stop using our services.
</p>

<h4 style={{ color: "#0a4d68", marginTop: "30px" }}>Browser Settings</h4>
<p>
  Most web browsers automatically accept Cookies. You can modify your browser settings or 
  use certain software tools to control how Cookies are accepted by your browser. However, 
  blocking FLUXLAB Essential Cookies may disable certain features of the 
  Website and impact your experience in using some of our services.
</p>
</div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div className="text-left">
              <div className="flex items-center gap-3 mb-4 cursor-pointer" onClick={scrollToTop}>
                <img
                  src={logo}
                  alt="FLUXLAB Logo"
                  className="w-[110px] h-auto"
                />
              </div>
              <p className="text-sm leading-relaxed">
                Leading manufacturer of automotive plastic components with a
                decade of excellence.
              </p>
            </div>

            {/* Quick Links */}
            <div className="text-left">
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="/#about"
                    className="hover:text-white transition-colors inline-block"
                  >
                    About Us
                  </a>
                </li>
                <li>
                  <a
                    href="/#services"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Services
                  </a>
                </li>
                <li>
                  <a
                    href="/#why-us"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Why Choose Us
                  </a>
                </li>
                <li>
                  <a
                    href="/#contact"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal Links */}
            <div className="text-left">
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="/Cookie"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Cookie of Service
                  </a>
                </li>
                <li>
                  <a
                    href="/privacy"
                    className="hover:text-white transition-colors inline-block"
                  >
                    Privacy Policy
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="text-left">
              <h4 className="text-white font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-2">
                  <Phone className="w-4 h-4 mt-1 flex-shrink-0 text-blue-500" />
                  <span>+91 9099159922</span>
                </li>
                <li className="flex items-start gap-2">
                  <Mail className="w-4 h-4 mt-1 flex-shrink-0 text-blue-500" />
                  <span>sales@fluxlab.in</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            <p>
              &copy; {new Date().getFullYear()} FLUXLAB. All rights reserved. | Precision Manufacturing Excellence
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Cookie;